import { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext({});

const CFG = {
  success: { bar: '#16a34a', icon: '#16a34a', label: 'Erfolg' },
  error:   { bar: '#dc2626', icon: '#dc2626', label: 'Fehler' },
  info:    { bar: '#1755f4', icon: '#1755f4', label: 'Hinweis' },
  warning: { bar: '#d97706', icon: '#d97706', label: 'Warnung' },
};

const ICONS = {
  success: (c) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  error: (c) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  info: (c) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  ),
  warning: (c) => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  ),
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counterRef = useRef(0);

  const addToast = useCallback((message, type = 'success', duration = 3800) => {
    const id = ++counterRef.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {/* Toast container */}
      <div style={{
        position: 'fixed', bottom: '24px', right: '24px',
        display: 'flex', flexDirection: 'column', gap: '8px',
        zIndex: 9999, pointerEvents: 'none',
        maxWidth: '360px', width: '100%',
      }}>
        {toasts.map(toast => {
          const cfg = CFG[toast.type] || CFG.info;
          return (
            <div
              key={toast.id}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                background: 'white',
                border: '1px solid #e8e8e8',
                borderLeft: `3px solid ${cfg.bar}`,
                padding: '13px 16px',
                minWidth: '260px', maxWidth: '360px',
                fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
                pointerEvents: 'all',
                boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
                animation: 'slideInToast 0.22s ease both',
              }}
            >
              <div style={{ flexShrink: 0, marginTop: '1px' }}>
                {ICONS[toast.type]?.(cfg.icon)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.5px', color: cfg.icon, marginBottom: '2px', textTransform: 'uppercase' }}>
                  {cfg.label}
                </div>
                <div style={{ fontSize: '13px', color: '#0d0d0d', lineHeight: 1.5 }}>
                  {toast.message}
                </div>
              </div>
              <button
                onClick={() => removeToast(toast.id)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: 0, color: '#c4c4c4', fontSize: '18px', lineHeight: 1,
                  flexShrink: 0, marginTop: '-1px',
                  transition: 'color 0.15s',
                }}
                onMouseEnter={e => e.target.style.color = '#888'}
                onMouseLeave={e => e.target.style.color = '#c4c4c4'}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes slideInToast {
          from { opacity: 0; transform: translateX(12px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
